import emitter from '@/services/emitter';
import { defineComponent, h, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, column, indexView, loader, pagination, tag, textColumn } from '@/ui/components';
import { bulkDelete, list as getEntityItemList, remove } from '@/services/api/resources/entityItem';
import { emptyList } from '@/services/api';
import { EntityItemList, EntityItemRecord } from '@/entities/entityItem';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { bulkDeleteButton } from '@/ui/components/Button';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { dataNameProp, filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { entityTypeFilter, searchFilter, userFilter } from '@/ui/components/filters/filters-horizontal';
import * as qs from '@/ui/queryState';
import { SelectOptions, SortDirection } from '@/ui/types';
import { useEntityItemCheck } from '@/ui/pages/entityItem/useEntityItemCheck';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import EntityItemEmpty from '@/ui/pages/entityItem/EntityItemEmpty.vue';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { filterFormLoader } from '@/ui/components/Loader';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityTypes } from '@/services/api/resources/entityType';

export const EntityItemIndexPage = defineComponent({
  name: 'EntityItemIndexPage',
  setup() {
    if (!hasFeature('entityItems')) {
      return renderResourceTeaserPage(t('common.entity-items'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_ITEMS) && !hasPermission(Permissions.MANAGE_ENTITY_PROPERTIES)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const search = qs.string('search', '');
    const entityType = qs.numbers('entityType', []);
    const user = qs.numbers('user', []);

    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useEntityItemCheck();

    const entityTypes = ref<SelectOptions>([]);
    const users = ref<SelectOptions>([]);

    const list = ref<EntityItemList>(emptyList());
    const getList = useTask(
      () => getEntityItemList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        entityType: entityType.value,
        entityFolder: [],
        user: user.value,
        sort: sort.value,
        dir: dir.value,
      }),
      entityItemList => {
        list.value = entityItemList;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, search, entityType, user], getList.run);

    onMounted(() => {
      emitter.on('reloadEntityItems', getList.run);
    });

    onBeforeUnmount(() => {
      emitter.off('reloadEntityItems', getList.run);
    });

    const { selectedResources, deleteBulkTask, onCheckedRowsUpdate, isBulkDeletionDisabled } = useBulkDeletable({
      getList,
      bulkDeleteFn: bulkDelete,
    });

    const getFilters = useTask(
      () => sequence(getAllEntityTypes(), getAllUsers()),
      ([entityTypeList, userList]) => {
        entityTypes.value = filterListToOptions(entityTypeList);
        users.value = filterListToOptions(userList);
      },
    );

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              entityTypeFilter(entityType, entityTypes.value, resetPagination),
              userFilter(user, users.value, resetPagination),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: 'entityItemEdit',
                    list: list.value.data,
                    deleteFn: remove,
                    selectedRecords: selectedResources.value,
                    loading: deleteBulkTask.state.value.pending,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('common.entity-items'),
                      singular: t('common.entity-item'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                  scopedSlots: {
                    title: ({ row: entityItem }: { row: EntityItemRecord }) => entityItem.data.title,
                    default: () => [
                      textColumn(
                        {
                          field: 'entityType',
                          label: t('common.entity-type'),
                        }, {
                          default: ({ row: entityItem }: { row: EntityItemRecord }) => entityItem.data.entityType
                            ? [tag('success', entityItem.data.entityType.data.title)]
                            : null,
                        },
                      ),
                      column({
                        field: 'description',
                        label: t('common.description'),
                        cellClass: 'break-word',
                      }, {
                        default: ({ row: group }: { row: EntityItemRecord }) => group.data.description,
                      }),
                      tagColumn({
                        field: 'users',
                        label: t('common.users'),
                      }, {
                        default: ({ row: group }: { row: EntityItemRecord }) =>
                          group.data.users.map(dataNameProp).join(', '),
                      }),
                    ],
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(EntityItemEmpty)]),
      ], false);
    };
  },
});
